import React, {useEffect, useMemo, useRef, useState} from 'react';
import './App.css';
import AppRoutes from "./common/routes/AppRoutes";
import {BrowserRouter, useLocation, useNavigate} from "react-router-dom";
import Constants from "./common/Constants";
import keycloakService, {KeycloakService} from "./keycloak/KeycloakService";
import {Provider} from "react-redux";
import store from "./store/Store";
import {StyledEngineProvider, ThemeProvider} from "@mui/material";
import theme from "./common/Theme";
import {LocalizationProvider} from "@mui/x-date-pickers/LocalizationProvider";
import tokenRefreshService, {TokenRefreshService} from "./common/TokenRefreshService";
import {KeycloakHolder} from "./keycloak/KeycloakHolder";
import LocaleContext, {Locale} from './common/LocaleContext';
import {Language, languageToLocale} from "./common/Language";
import userSettingsService from "./common/apis/user/UserSettingsService";
import EventManagerContext from './common/event/EventManagerContext';
import EventManager from "./common/event/EventManager";
import ConfigurationContext, {Configuration} from './common/ConfigurationContext';
import {FeatureStatusesDto} from "./configuration/FeatureStatusesDto";
import inaccessibleRouteService from "./common/routes/service/InaccessibleRouteService";
import {IPropertyDefinition} from "./common/apis/CommonTypes";
import elementService from "./pages/main/content/elementdetail/service/ElementService";
import ElementPropertyDefinitionContext, {ElementPropertyDefinition} from "./common/ElementPropertyDefinitionContext";
import diagramService from "./pages/main/content/diagrams/service/DiagramService";
import DiagramPropertyDefinitionContext from './common/DiagramPropertyDefinitionContext';
import {QueryClient, QueryClientProvider} from '@tanstack/react-query';
import {AdapterDayjs} from "@mui/x-date-pickers/AdapterDayjs";
import {DateTimeSubsystemConfigurer} from "./common/DateTimeSubsystemConfigurer";

type Props = {
    keycloakService: KeycloakService,
    tokenRefreshService: TokenRefreshService,
}

function App({keycloakService, tokenRefreshService}: Readonly<Props>) {
    const navigate = useNavigate();
    const location = useLocation();

    const isAuthRequired = location.pathname !== Constants.FE_APP_DIAGRAM_EXPORT_VIEW_ROUTE;
    const isAuthenticated = KeycloakHolder.keycloak && KeycloakHolder.getParsedToken() != null;

    useEffect(() => {
        function initTokenRefresh(onRefreshFailed: () => void) {
            if (KeycloakHolder.keycloak && KeycloakHolder.getParsedToken() != null) {
                tokenRefreshService.planTokenRefresh(KeycloakHolder.keycloak, KeycloakHolder.getParsedToken()!, onRefreshFailed);
            } else {
                throw new Error();
            }
        }

        if (!isAuthenticated && isAuthRequired) {
            (async () => {
                try {
                    const config = await keycloakService.fetchKeycloakConfig();
                    await keycloakService.doKeycloakLogin(config);
                    initTokenRefresh(() => {
                        navigate(Constants.FE_APP_LOGIN_FAILED_ROUTE);
                    });
                    inaccessibleRouteService.saveCurrent();
                    navigate(Constants.FE_APP_LOGIN_ROUTE);
                } catch (error) {
                    navigate(Constants.FE_APP_LOGIN_FAILED_ROUTE);
                }
            })();
        }
    }, [keycloakService, tokenRefreshService, isAuthenticated, isAuthRequired, navigate]);

    if (!isAuthenticated && isAuthRequired) {
        return <div/>;
    }
    return (
        <AppRoutes/>
    );
}

export default function AppWrapper() {
    const [language, setLanguage] = useState<Language>(Language.CS);
    const [featureStatuses, setFeatureStatuses] = useState<FeatureStatusesDto>({
        aiEnabled: false,
        graphQueryEnabled: false,
    });
    const [elementPropertyDefinitions, setElementPropertyDefinitions] = useState<IPropertyDefinition[]>([]);
    const [diagramsPropertyDefinitions, setDiagramsPropertyDefinitions] = useState<IPropertyDefinition[]>([]);

    const eventManager = useRef<EventManager>(new EventManager());

    const locale: Locale = useMemo(() => ({
        locale: languageToLocale(language) as string,
        language: language,
        setLanguage: updateLanguage
    }), [language]);

    const adapterLocale = useMemo(() => {
        return languageToLocale(language);
    }, [language]);

    useEffect(() => {
        DateTimeSubsystemConfigurer.initializeDateTimeSubsystem();
        DateTimeSubsystemConfigurer.setLocaleByLanguage(language);
    }, [language]);

    function updateLanguage(language: Language, store: boolean) {
        if (store) {
            userSettingsService.updateGeneralSettings({
                language: language
            });
        }
        setLanguage(language);
    }

    const configuration: Configuration = {
        featureStatuses: featureStatuses,
        setFeatureStatuses: setFeatureStatuses
    };

    const queryClient = new QueryClient();

    const elementPropertyDefinition: ElementPropertyDefinition = {
        propertyDefinitions: elementPropertyDefinitions,
        updatePropertyDefinitions: updateElementPropertyDefinitions,
    }

    function updateElementPropertyDefinitions() {
        elementService.findAllPropertyDefinitions()
            .then(propertyDefinitions => {
                setElementPropertyDefinitions(propertyDefinitions);
            });
    }

    const diagramPropertyDefinition: ElementPropertyDefinition = {
        propertyDefinitions: diagramsPropertyDefinitions,
        updatePropertyDefinitions: updateDiagramPropertyDefinitions,
    }

    function updateDiagramPropertyDefinitions() {
        diagramService.findAllPropertyDefinitions()
            .then(propertyDefinitions => {
                setDiagramsPropertyDefinitions(propertyDefinitions);
            });
    }

    return (
        <ElementPropertyDefinitionContext.Provider value={elementPropertyDefinition}>
            <DiagramPropertyDefinitionContext.Provider value={diagramPropertyDefinition}>
                <ConfigurationContext.Provider value={configuration}>
                    <QueryClientProvider client={queryClient}>
                        <LocaleContext.Provider value={locale}>
                            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={adapterLocale}>
                                <Provider store={store as any}>
                                    <StyledEngineProvider injectFirst>
                                        <ThemeProvider theme={theme}>
                                            <EventManagerContext.Provider value={eventManager.current}>
                                                <BrowserRouter>
                                                    <App keycloakService={keycloakService}
                                                         tokenRefreshService={tokenRefreshService}/>
                                                </BrowserRouter>
                                            </EventManagerContext.Provider>
                                        </ThemeProvider>
                                    </StyledEngineProvider>
                                </Provider>
                            </LocalizationProvider>
                        </LocaleContext.Provider>
                    </QueryClientProvider>
                </ConfigurationContext.Provider>
            </DiagramPropertyDefinitionContext.Provider>
        </ElementPropertyDefinitionContext.Provider>
    );
}
